import { type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import get from 'lodash/get';
import { InteractiveText } from '../../Text';
import { type RichTextStyle } from '../RichText';

type NestedKeys<T, P extends string = ''> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? P extends ''
          ? NestedKeys<T[K], K>
          : NestedKeys<T[K], `${P}.${K}`>
        : never;
    }[keyof T]
  : P;

const styles: Record<
  'active' | 'inactive',
  Record<
    RichTextStyle,
    {
      color: NestedKeys<Theme['colors']>;
      underlineColor: NestedKeys<Theme['colors']>;
      underlineHoverColor: NestedKeys<Theme['colors']>;
    }
  >
> = {
  active: {
    alternative: {
      color: 'neutral.white100',
      underlineColor: 'neutral.white100',
      underlineHoverColor: 'brand.green80',
    },
    muted: {
      color: 'neutral.grey160',
      underlineColor: 'brand.green80',
      underlineHoverColor: 'brand.green80',
    },
    primary: {
      color: 'neutral.grey160',
      underlineColor: 'brand.green80',
      underlineHoverColor: 'brand.green80',
    },
  },
  inactive: {
    alternative: {
      color: 'neutral.white100',
      underlineColor: 'neutral.white100',
      underlineHoverColor: 'neutral.white100',
    },
    muted: {
      color: 'neutral.grey140',
      underlineColor: 'neutral.grey120',
      underlineHoverColor: 'neutral.grey120',
    },
    primary: {
      color: 'neutral.grey140',
      underlineColor: 'neutral.grey120',
      underlineHoverColor: 'neutral.grey120',
    },
  },
};

const ButtonBase = styled.button<{
  textStyle: RichTextStyle;
}>`
  white-space: nowrap;
  position: relative;
  cursor: pointer;
  border: 0;
  padding: ${({ theme }) => theme.spacing[5]}
    ${({ theme }) => theme.spacing[20]};
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.brand.green20};

  &:focus-visible {
    outline: 0;

    &::before {
      content: '';
      position: absolute;
      inset: -0.1rem;
      border-radius: 999px;
      outline: 0.125rem dashed ${({ theme }) => theme.colors.brand.orange100};
    }
  }
`;
//   &::after {
//     background-color: ${({ theme, textStyle }) =>
//       get(theme.colors, styles.active[textStyle].underlineColor)};
//     border-top-left-radius: ${({ theme }) => theme.radius.xxs};
//     border-top-right-radius: ${({ theme }) => theme.radius.xxs};
//     content: '';
//     transform: ${({ theme }) => `translateY(${theme.spacing[40]})`};
//     position: absolute;
//     bottom: ${({ theme }) => theme.spacing[40]};
//     left: 0;
//     height: ${({ theme }) => theme.spacing[10]};
//     width: 100%;
//   }
// `;

const ActiveButton = styled(ButtonBase)`
  ${ButtonBase};

  color: ${({ textStyle, theme }) =>
    get(theme.colors, styles.active[textStyle].color)};
  background-color: ${({ theme }) => theme.colors.brand.green60};
`;

const InactiveButton = styled(ButtonBase)`
  ${ButtonBase};

  color: ${({ textStyle, theme }) =>
    get(theme.colors, styles.inactive[textStyle].color)};

  &:hover {
    color: ${({ textStyle, theme }) =>
      get(theme.colors, styles.active[textStyle].color)};
    background-color: ${({ theme }) => theme.colors.brand.green40};

    &::after {
      display: initial;
      background-color: ${({ theme, textStyle }) =>
        get(theme.colors, styles.inactive[textStyle].underlineHoverColor)};
    }
  }

  &::after {
    display: none;
  }
`;

const StyledInteractiveText = styled(InteractiveText)`
  font-size: 1rem;
  line-height: 1.5em;
`;

export { ActiveButton, InactiveButton, StyledInteractiveText };
